// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the server-driven Claude sign-in (open the link, paste the code) as a hook, so the
// connect modal and the Konvo panel's inline connect step share one flow. The connect modal
// prepares its link when it opens and abandons it on close. The panel passes a `keep`
// holder: the link lives for the panel session (asked for once, kept across minimise and
// remounts) and the panel abandons it when the session ends. Only a link this hook (or its
// holder) started is ever abandoned.

import KonvoConnections, {KonvoClaudeConnectError} from '@app/features/konvo_connections/state/KonvoConnections';
import {useCallback, useEffect, useRef, useState} from 'react';

type Link = {id: string; authUrl: string};

export interface ClaudeLink {
	link: Link | null;
	starting: boolean;
	code: string;
	setCode: (code: string) => void;
	error: string | null;
	setError: (error: string | null) => void;
	submitting: boolean;
	/** Prepares a fresh sign-in link (also after an expired or refused code). */
	start: () => Promise<void>;
	/** Exchanges the pasted code; resolves true once Claude is connected. */
	submit: () => Promise<boolean>;
	canSubmit: boolean;
}

/** Holds the link outside the component (the Konvo panel keeps one per session). */
export interface ClaudeLinkHolder {
	link: Link | null;
	/** A link was already asked for: do not start another one on mount. */
	started: boolean;
	markStarted: () => void;
	setLink: (link: Link | null) => void;
}

export function useClaudeLink(
	options: {autoStart?: boolean; onConnected?: () => void; keep?: ClaudeLinkHolder} = {},
): ClaudeLink {
	const {autoStart = true, keep} = options;
	const [link, setLinkState] = useState<Link | null>(() => keep?.link ?? null);
	const [starting, setStarting] = useState(false);
	const [code, setCodeState] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const onConnected = useRef(options.onConnected);
	onConnected.current = options.onConnected;
	const keepRef = useRef(keep);
	keepRef.current = keep;
	/** The link this hook started and has not finished or abandoned yet (without a holder). */
	const ownLink = useRef<string | null>(null);
	const mounted = useRef(true);
	const attempt = useRef(0);

	const setLink = useCallback((next: Link | null) => {
		setLinkState(next);
		keepRef.current?.setLink(next);
	}, []);

	const start = useCallback(async () => {
		const current = ++attempt.current;
		keepRef.current?.markStarted();
		setStarting(true);
		setError(null);
		setLink(null);
		setCodeState('');
		try {
			const next = await KonvoConnections.startClaudeLink();
			if (!mounted.current && !keepRef.current) {
				// Closed while the link was being made: abandon it straight away.
				KonvoConnections.cancelClaudeLink();
				return;
			}
			if (current !== attempt.current) return;
			if (!keepRef.current) ownLink.current = next.id;
			setLink(next);
		} catch (err) {
			if (current !== attempt.current || !mounted.current) return;
			setError(err instanceof KonvoClaudeConnectError ? err.message : 'Could not start Claude sign-in.');
		} finally {
			if (mounted.current && current === attempt.current) setStarting(false);
		}
	}, [setLink]);

	useEffect(() => {
		mounted.current = true;
		// The panel asks once per session; the modal every time it opens.
		if (autoStart && !keepRef.current?.started) void start();
		return () => {
			mounted.current = false;
			// Without a holder the link dies with the component; with one, the holder decides.
			if (!keepRef.current && ownLink.current) KonvoConnections.cancelClaudeLink();
			ownLink.current = null;
		};
	}, []);

	const setCode = useCallback((next: string) => {
		setCodeState(next);
		setError(null);
	}, []);

	const submit = useCallback(async (): Promise<boolean> => {
		if (!link || !code.trim()) return false;
		setSubmitting(true);
		setError(null);
		try {
			await KonvoConnections.submitClaudeLinkCode(link.id, code.trim());
			// Used up: nothing left to abandon.
			ownLink.current = null;
			onConnected.current?.();
			return true;
		} catch (err) {
			const failure = err instanceof KonvoClaudeConnectError ? err : null;
			if (mounted.current) setError(failure?.message ?? 'Could not connect Claude. Please try again.');
			if (failure && ['no_session', 'superseded', 'timeout', 'claude_rejected_code'].includes(failure.code)) {
				ownLink.current = null;
				setLink(null);
			}
			return false;
		} finally {
			if (mounted.current) setSubmitting(false);
		}
	}, [link, code, setLink]);

	return {
		link,
		starting,
		code,
		setCode,
		error,
		setError,
		submitting,
		start,
		submit,
		canSubmit: !!link && !!code.trim() && !submitting,
	};
}
